/* [2.8] np-product-info – variant re-render + #np-… anchor opening. Loaded once per page. */
(() => {
  if (window.NpProductInfo) return;
  window.NpProductInfo = true;

  const controllers = new WeakMap();

  function refresh(root, variantId) {
    const sectionId = root.dataset.npVariantSection;
    const url = `${root.dataset.productUrl}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(sectionId)}`;
    const previous = controllers.get(root);
    if (previous) previous.abort();
    const controller = new AbortController();
    controllers.set(root, controller);

    fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-np-variant-section="${sectionId}"] [data-np-swap-root]`);
        const current = root.querySelector('[data-np-swap-root]');
        if (!fresh || !current) return;
        // keep the customer's open/closed state per accordion id
        const state = {};
        current.querySelectorAll('details[id]').forEach((d) => { state[d.id] = d.open; });
        fresh.querySelectorAll('details[id]').forEach((d) => {
          if (Object.prototype.hasOwnProperty.call(state, d.id)) d.open = state[d.id];
        });
        current.innerHTML = fresh.innerHTML;
        enhance(root);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        console.error(error);
      });
  }

  document.addEventListener('variant:change', (event) => {
    const detail = event.detail || {};
    if (!detail.variant || !detail.variant.id) return;
    if (detail.formElement && detail.formElement.closest && detail.formElement.closest('quick-add-modal')) return;
    document.querySelectorAll('[data-np-variant-section]').forEach((root) => refresh(root, detail.variant.id));
  });

  /* [PDP-layout] desktop = open panels · jump menu · "Vis alle … / Vis mindre" on every card */
  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let navLockUntil = 0;

  function clamp(scope) {
    scope.querySelectorAll('[data-np-more]').forEach((box) => {
      const toggle = box.parentElement.querySelector('[data-np-more-toggle]');
      if (!toggle || box.classList.contains('is-expanded')) return;
      if (!box.offsetParent) return; // inside a closed card: measured when it opens
      box.classList.add('is-clamped');
      const needed = box.scrollHeight > box.clientHeight + 40;
      box.classList.toggle('is-clamped', needed);
      toggle.hidden = !needed;
    });
  }

  /* Desktop: make both columns end on the same line. Reset the folded cards to their normal height,
     measure, then let the shorter column's folded card show as much more as the height difference. */
  function balance(info) {
    const main = info.querySelector('.np-info__main');
    const side = info.querySelector('.np-info__side');
    const boxes = info.querySelectorAll('.np-info__layout [data-np-more]');
    boxes.forEach((box) => box.style.removeProperty('--np-clamp'));
    clamp(info);
    if (!desktop.matches || !main || !side) return;

    info.classList.add('np-info--measuring');
    const diff = main.offsetHeight - side.offsetHeight;
    const column = diff > 0 ? side : main;
    const grow = column.querySelector('.np-acc--description [data-np-more].is-clamped:not(.is-expanded)') ||
      column.querySelector('.np-acc--specs [data-np-more].is-clamped:not(.is-expanded)') ||
      column.querySelector('[data-np-more].is-clamped:not(.is-expanded)');
    if (grow && Math.abs(diff) > 1) {
      const toggle = grow.parentElement.querySelector('[data-np-more-toggle]');
      const toggleSpace = toggle && !toggle.hidden ? toggle.offsetHeight + parseFloat(getComputedStyle(toggle).marginTop || 0) : 0;
      const target = grow.clientHeight + Math.abs(diff);
      if (grow.scrollHeight <= target + toggleSpace) {
        /* everything fits: show it all, no button */
        grow.classList.remove('is-clamped');
        if (toggle) toggle.hidden = true;
      } else {
        grow.style.setProperty('--np-clamp', target + 'px');
      }
    }
    info.classList.remove('np-info--measuring');
  }

  let balanceFrame;
  function balanceAll() {
    cancelAnimationFrame(balanceFrame);
    balanceFrame = requestAnimationFrame(() => document.querySelectorAll('.np-info').forEach(balance));
  }

  function setActive(info, id) {
    info.querySelectorAll('[data-np-nav]').forEach((a) => {
      if (a.dataset.npNav === id) {
        a.setAttribute('aria-current', 'true');
        const list = a.closest('ul');
        if (list && list.scrollWidth > list.clientWidth) {
          list.scrollTo({ left: a.offsetLeft - 16, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }
      } else {
        a.removeAttribute('aria-current');
      }
    });
  }

  const observers = new WeakMap();
  function watchNav(info) {
    if (observers.get(info)) observers.get(info).disconnect();
    const links = info.querySelectorAll('[data-np-nav]');
    if (!links.length || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      if (Date.now() < navLockUntil) return;
      entries.forEach((entry) => { if (entry.isIntersecting) setActive(info, entry.target.id); });
    }, { rootMargin: '-25% 0px -60% 0px' });
    links.forEach((a) => { const el = document.getElementById(a.dataset.npNav); if (el) observer.observe(el); });
    observers.set(info, observer);
  }

  function enhance(root) {
    const info = root.closest('.np-info') || root;
    info.classList.toggle('np-info--panels', desktop.matches);
    if (desktop.matches) info.querySelectorAll('details[data-np-acc]').forEach((d) => { d.open = true; });
    balance(info);
    watchNav(info);
  }

  function jumpTo(info, id, link) {
    const target = document.getElementById(id);
    if (!target) return;
    if (target.tagName === 'DETAILS') target.open = true;
    clamp(target);
    const nav = info.querySelector('.np-info__nav');
    const offset = (nav ? nav.offsetHeight : 0) + 16;
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
    /* scrolling up would make the sticky header slide in over the content – keep it hidden for this jump */
    if (top < window.scrollY) {
      const header = document.querySelector('sticky-header');
      if (header) header.dispatchEvent(new Event('preventHeaderReveal'));
    }
    navLockUntil = Date.now() + 1200;
    setActive(info, id);
    window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    if (history.replaceState) history.replaceState(null, '', '#' + id);
    if (link) target.querySelector('summary')?.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;
    const navLink = event.target.closest('[data-np-nav]');
    if (navLink) {
      event.preventDefault();
      jumpTo(navLink.closest('.np-info'), navLink.dataset.npNav, navLink);
      return;
    }
    const toggle = event.target.closest('[data-np-more-toggle]');
    if (toggle) {
      const box = toggle.parentElement.querySelector('[data-np-more]');
      const open = !box.classList.contains('is-expanded');
      box.classList.toggle('is-expanded', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      const label = toggle.querySelector('[data-np-more-label]');
      if (label) label.textContent = open ? toggle.dataset.labelLess : toggle.dataset.labelMore;
      const info = box.closest('.np-info');
      if (info) balance(info);
      if (!open) {
        const card = box.closest('details');
        if (card && card.getBoundingClientRect().top < 0) jumpTo(card.closest('.np-info'), card.id);
      }
      return;
    }
    /* desktop panels do not collapse */
    if (event.target.closest('.np-info--panels .np-acc__summary')) event.preventDefault();
  });

  /* a card opened on a phone: measure it now that it is visible */
  document.addEventListener('toggle', (event) => {
    if (event.target.matches && event.target.matches('details[data-np-acc]') && event.target.open) clamp(event.target);
  }, true);

  function enhanceAll() { document.querySelectorAll('[data-np-variant-section] [data-np-swap-root]').forEach(enhance); }
  desktop.addEventListener('change', enhanceAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enhanceAll);
  else enhanceAll();
  /* images, fonts and window size change heights */
  window.addEventListener('load', balanceAll);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(balanceAll);
  let resizeTimer;
  window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(balanceAll, 150); }, { passive: true });

  function openTarget(hash) {
    if (!hash || hash.length < 2) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el && el.tagName === 'DETAILS') el.open = true;
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href^="#np-"]');
    if (link) openTarget(link.getAttribute('href'));
  });
  window.addEventListener('hashchange', () => openTarget(window.location.hash));
  openTarget(window.location.hash);
})();
